import { Button } from "@components/common/button"
import { DictType } from "@pma/types"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@components/common/alert-dialog"
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { toast } from "@hooks/use-toast"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { CLONE_PROJECT_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"
import { Trash2Icon } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import { PROJECTS_PAGE_ROUTE } from "@pma/utils/page-routes"
import { HTMLAttributes } from "react"
import useTranslator from "@hooks/use-translator"

interface Props extends HTMLAttributes<HTMLDivElement> {
  project: DictType
}

const CloneProject = ({ project, children, className }: Props) => {
  const navigate = useNavigate()
  const { translate } = useTranslator()

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const cloneProject = async () => {
    const response = await axios.post(
      SERVER_URL + CLONE_PROJECT_ENDPOINT,
      {
        project_id: project?.id,
        with_tasks: true,
        with_subtasks: true,
        with_users: true,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    return response.data
  }

  const cloneProjectMutation = useMutation({
    mutationKey: ["clone-project", project],
    mutationFn: cloneProject,
    onSuccess: (data) => {
      navigate(PROJECTS_PAGE_ROUTE + `/${data?.id}`)
      toast({
        title: translate("Success"),
        description: translate("The Project is clone successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to clone the project"),
        variant: "error",
      })
    },
  })
  return (
    <AlertDialog>
      <AlertDialogTrigger className={className}>
        <Tooltip>
          <TooltipTrigger>
            {children ? (
              children
            ) : (
              <Button variant={"ghost"} className='text-xs text-red-500' size={"icon"}>
                <Trash2Icon size={14} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className='flex items-center gap-1'>
            <h4>
              {translate(`Clone`)} {project?.title}
            </h4>
          </TooltipContent>
        </Tooltip>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {translate("Proceed to clone")} {project?.title}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {translate(
              "This action will create a copy of the project with all the tasks and members",
            )}
            .{" "}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{translate("Cancel")}</AlertDialogCancel>
          <AlertDialogAction
            className='bg-primary'
            disabled={cloneProjectMutation?.isPending}
            onClick={() => cloneProjectMutation.mutate()}
          >
            {translate("Continue")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default CloneProject
